(() => {
  'use strict';

  const packPriority = (count) => ({ 12: 0, 5: 0, 4: 1, 2: 2, 1: 3, 24: 4 })[count] ?? (count > 1 ? 1 : 3);
  const interpolate = (message, values) => Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(String(value)), message);

  function initProduct(root) {
    if (root.dataset.productReady === 'true') return;
    const source = root.querySelector('[data-product-json]');
    const form = root.querySelector('.product-form');
    if (!source || !form) return;
    let data;
    try {
      data = JSON.parse(source.content?.textContent ?? source.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(data.variants) || data.variants.length === 0) return;
    const variants = data.variants;
    const groups = [...root.querySelectorAll('[data-option-group]')];
    const variantInput = form.querySelector('[data-variant-id]');
    const fallbackSelect = form.querySelector('[data-variant-select]');
    const button = form.querySelector('[data-add-to-cart]');
    const quantity = form.querySelector('[data-product-quantity]');
    const error = form.querySelector('[data-product-error]');
    const nameInput = form.querySelector('[data-personalisation-name]');
    const personalisation = form.querySelector('[data-personalisation]');
    let selected = variants.find((variant) => variant.id === data.selectedId) || variants.find((variant) => variant.available) || variants[0];
    if (!variantInput || !button || !quantity || !error) return;

    const optionMatches = (variant, options) => variant.options.every((value, index) => value === options[index]);
    const otherOptionsMatch = (variant, options, excludedIndex) => variant.options.every((value, index) => index === excludedIndex || value === options[index]);
    const prefixMatches = (variant, options, endIndex) => variant.options.every((value, index) => index > endIndex || value === options[index]);

    function bestCandidate(candidates, preferred) {
      return [...candidates].sort((a, b) => {
        const aMatches = a.options.reduce((sum, value, index) => sum + Number(value === preferred[index]), 0);
        const bMatches = b.options.reduce((sum, value, index) => sum + Number(value === preferred[index]), 0);
        return bMatches - aMatches || packPriority(a.count) - packPriority(b.count);
      })[0];
    }

    function setError(message = '') {
      error.textContent = message;
      error.hidden = !message;
    }

    const gallery = root.querySelector('[data-gallery]');
    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    const thumbnails = [...root.querySelectorAll('[data-gallery-thumbnail]')];
    let activeImage = Math.max(0, slides.findIndex((slide) => !slide.hidden));

    function showImage(index, moveFocus = false) {
      if (slides.length === 0) return;
      activeImage = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== activeImage; });
      const currentImageId = slides[activeImage].dataset.imageId;
      thumbnails.forEach((thumbnail) => {
        if (thumbnail.dataset.imageId === currentImageId) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      const currentThumbnail = thumbnails.find((thumbnail) => thumbnail.dataset.imageId === currentImageId);
      const enlarge = gallery?.querySelector('[data-gallery-enlarge]');
      if (enlarge && currentThumbnail) enlarge.href = currentThumbnail.href;
      const counter = gallery?.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = interpolate(data.strings.imagePosition, { position: activeImage + 1, total: slides.length });
      if (moveFocus && currentThumbnail) {
        currentThumbnail.focus({ preventScroll: true });
        currentThumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }
    }

    function renderPicker() {
      const packVariants = data.packOptionIndex >= 0
        ? variants.filter((variant) => variant.available && otherOptionsMatch(variant, selected.options, data.packOptionIndex))
        : [];
      const lowestUnitPrice = packVariants.length ? Math.min(...packVariants.map((variant) => variant.price / variant.count)) : null;
      const single = packVariants.filter((variant) => variant.count === 1).sort((a, b) => a.price - b.price)[0];
      const discountMinimum = Number(data.singleDiscount?.minimum);
      const discountPercent = Number(data.singleDiscount?.percent);
      const discountKnown = Number.isInteger(discountMinimum) && discountMinimum >= 2 && Number.isFinite(discountPercent) && discountPercent >= 0 && discountPercent < 100;
      const packQuantity = Number.isInteger(Number(quantity.value)) && Number(quantity.value) > 0 ? Number(quantity.value) : 1;
      const singleBenchmark = (count) => single && discountKnown
        ? single.price * (count * packQuantity >= discountMinimum ? 1 - discountPercent / 100 : 1)
        : null;
      groups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const inputs = [...group.querySelectorAll('[data-option-input]')];
        inputs.forEach((input) => {
          const option = input.closest('.variant-option');
          const desired = [...selected.options];
          desired[index] = input.value;
          const allowed = variants.filter((variant) => variant.available && prefixMatches(variant, desired, index));
          const exact = variants.find((variant) => optionMatches(variant, desired));
          const candidate = exact?.available ? exact : bestCandidate(allowed, desired);
          input.checked = selected.options[index] === input.value;
          input.disabled = allowed.length === 0 && !input.checked;
          option.classList.toggle('is-selected', input.checked);
          option.classList.toggle('is-unavailable', allowed.length === 0);
          const unavailable = option.querySelector('[data-option-unavailable]');
          if (unavailable) unavailable.hidden = allowed.length > 0;
          const meta = option.querySelector('[data-option-meta]');
          const badge = option.querySelector('[data-option-badge]');
          if (meta) {
            meta.textContent = candidate ? `${candidate.priceFormatted} · ${candidate.unitFormatted}` : '';
            meta.hidden = !candidate;
          }
          if (badge) {
            const isBest = candidate && candidate.count > 1 && lowestUnitPrice !== null
              && candidate.price / candidate.count === lowestUnitPrice
              && singleBenchmark(candidate.count) !== null && lowestUnitPrice < singleBenchmark(candidate.count);
            const isRecommended = data.handle === 'bamboo-toothbrush' && candidate?.count === 12;
            badge.textContent = isBest ? data.strings.bestValue : isRecommended ? data.strings.stockUp : '';
            badge.hidden = !badge.textContent;
          }
          if (index === data.packOptionIndex) {
            const anyVariant = variants.find((variant) => variant.options[index] === input.value);
            option.dataset.packCount = String(anyVariant?.count || 1);
            option.hidden = Number(option.dataset.packCount) === 24 && allowed.length === 0 && !input.checked;
          }
        });
        if (index === data.packOptionIndex) {
          const container = group.querySelector('[data-option-values]');
          [...container.children].sort((a, b) => packPriority(Number(a.dataset.packCount)) - packPriority(Number(b.dataset.packCount))).forEach((option) => container.append(option));
        }
      });
      const saving = root.querySelector('[data-product-saving]');
      if (saving) {
        const baseline = singleBenchmark(selected.count);
        const percent = baseline && selected.count > 1 ? Math.round((1 - selected.price / (baseline * selected.count)) * 100) : 0;
        saving.textContent = percent > 0 ? interpolate(data.strings.saving, { percent }) : '';
        saving.hidden = !saving.textContent;
      }
    }

    function renderSelection(updateUrl = false) {
      variantInput.value = selected.id;
      if (fallbackSelect) fallbackSelect.value = selected.id;
      root.querySelectorAll('[data-product-price], [data-mobile-price]').forEach((price) => { price.textContent = selected.priceFormatted; });
      const unitPrice = root.querySelector('[data-product-unit-price]');
      if (unitPrice) unitPrice.textContent = selected.unitFormatted;
      const stock = root.querySelector('[data-product-stock]');
      if (stock) stock.textContent = selected.available ? data.strings.available : data.strings.soldOut;
      button.disabled = !selected.available;
      button.textContent = selected.available ? data.strings.addToCart : data.strings.soldOut;
      if (personalisation && nameInput) {
        const personalised = data.personalisationIndex >= 0 && selected.options[data.personalisationIndex].toLowerCase() === 'yes';
        personalisation.hidden = !personalised;
        nameInput.disabled = !personalised;
        nameInput.required = personalised;
        if (!personalised) nameInput.setCustomValidity('');
      }
      const summary = root.querySelector('[data-pack-summary]');
      if (summary && data.packOptionIndex >= 0) summary.textContent = interpolate(data.handle === 'bamboo-toothbrush' ? data.strings.brushSummary : data.strings.packSummary, { count: selected.count });
      renderPicker();
      const imageIndex = slides.findIndex((slide) => slide.dataset.imageId === selected.imageId);
      if (imageIndex >= 0) showImage(imageIndex);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selected.id);
        url.searchParams.delete('option_values');
        window.history.replaceState(window.history.state, '', url);
      }
    }

    groups.forEach((group) => {
      group.addEventListener('change', (event) => {
        const input = event.target.closest('[data-option-input]');
        if (!input) return;
        const index = Number(input.dataset.optionIndex);
        const desired = [...selected.options];
        desired[index] = input.value;
        const exact = variants.find((variant) => variant.available && optionMatches(variant, desired));
        const candidates = variants.filter((variant) => variant.available && prefixMatches(variant, desired, index));
        const next = exact || bestCandidate(candidates, desired);
        if (!next) {
          renderSelection();
          setError(data.strings.invalidSelection);
          return;
        }
        selected = next;
        setError();
        renderSelection(true);
      });
    });

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const index = slides.findIndex((slide) => slide.dataset.imageId === thumbnail.dataset.imageId);
        if (index < 0) return;
        event.preventDefault();
        showImage(index);
      });
      thumbnail.addEventListener('keydown', (event) => {
        const currentIndex = slides.findIndex((slide) => slide.dataset.imageId === thumbnail.dataset.imageId);
        const nextIndex = { ArrowLeft: currentIndex - 1, ArrowRight: currentIndex + 1, Home: 0, End: slides.length - 1 }[event.key];
        if (nextIndex === undefined) return;
        event.preventDefault();
        showImage(nextIndex, true);
      });
    });
    gallery?.querySelector('[data-gallery-previous]')?.addEventListener('click', () => showImage(activeImage - 1));
    gallery?.querySelector('[data-gallery-next]')?.addEventListener('click', () => showImage(activeImage + 1));
    gallery?.querySelector('[data-gallery-controls]')?.removeAttribute('hidden');
    showImage(activeImage);

    const decrease = form.querySelector('[data-quantity-decrease]');
    const increase = form.querySelector('[data-quantity-increase]');
    const setQuantity = (change) => {
      const current = Number(quantity.value);
      quantity.value = String(Math.max(1, (Number.isInteger(current) && current > 0 ? current : 1) + change));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    };
    decrease?.addEventListener('click', () => setQuantity(-1));
    increase?.addEventListener('click', () => setQuantity(1));
    quantity.addEventListener('input', renderPicker);
    quantity.addEventListener('change', renderPicker);
    decrease?.removeAttribute('hidden');
    increase?.removeAttribute('hidden');
    nameInput?.addEventListener('input', () => nameInput.setCustomValidity(''));

    form.addEventListener('submit', (event) => {
      setError();
      if (!selected.available || variantInput.value !== selected.id) {
        event.preventDefault();
        setError(data.strings.invalidSelection);
        return;
      }
      if (nameInput?.required) {
        nameInput.value = nameInput.value.trim();
        if (!nameInput.value) {
          event.preventDefault();
          nameInput.setCustomValidity(data.strings.nameRequired);
          nameInput.reportValidity();
          setError(data.strings.nameRequired);
          return;
        }
      }
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
      }
    });

    const mobileBuy = root.querySelector('[data-mobile-buy]');
    const mobileLink = root.querySelector('[data-mobile-choose]');
    mobileLink?.addEventListener('click', () => requestAnimationFrame(() => groups[0]?.querySelector('input:checked')?.focus({ preventScroll: true })));
    if (mobileBuy && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        mobileBuy.hidden = entry.isIntersecting;
      }, { threshold: 0 });
      observer.observe(button);
    }

    renderSelection();
    if (fallbackSelect) fallbackSelect.disabled = true;
    variantInput.disabled = false;
    form.querySelector('[data-variant-fallback]')?.setAttribute('hidden', '');
    form.querySelector('[data-variant-picker]')?.removeAttribute('hidden');
    root.dataset.productReady = 'true';
    root.classList.add('js-ready');
  }

  const init = (scope = document) => scope.querySelectorAll('[data-eeco-product]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
